import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    MessageSquare, 
    Send, 
    SkipForward, 
    Sparkles, 
    Edit3, 
    Check,
    ChevronRight,
    Loader2,
    Lightbulb
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ClarificationFlow = ({ 
    originalIdea, 
    onComplete, 
    onSkip, 
    isEnabled = true 
}) => {
    const { isAuthenticated, getAuthHeaders } = useAuth();
    const [stage, setStage] = useState('evaluating'); // evaluating, questioning, refining, reviewing
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [currentAnswer, setCurrentAnswer] = useState('');
    const [refinedPrompt, setRefinedPrompt] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [analysis, setAnalysis] = useState('');
    const [clarityScore, setClarityScore] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);
    const chatEndRef = useRef(null);

    // Auto-scroll to bottom of chat
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [answers, currentQuestionIndex, stage]);

    // Focus input when question changes
    useEffect(() => {
        if (stage === 'questioning' && inputRef.current) {
            inputRef.current.focus();
        }
    }, [currentQuestionIndex, stage]);

    // Evaluate the prompt when component mounts
    useEffect(() => {
        if (isEnabled && originalIdea) {
            evaluatePrompt();
        } else if (!isEnabled) {
            // Skip clarification if disabled
            onComplete(originalIdea, false);
        }
    }, [originalIdea, isEnabled]);

    const evaluatePrompt = async () => {
        setIsLoading(true);
        setError(null);
        setStage('evaluating');

        try {
            const headers = isAuthenticated ? getAuthHeaders() : {};
            const response = await axios.post(
                `${BACKEND_URL}/api/clarify/evaluate`,
                { idea: originalIdea },
                { headers }
            );

            const { needs_clarification, clarity_score, questions: qs, analysis: a } = response.data;
            
            setClarityScore(clarity_score);
            setAnalysis(a);

            if (needs_clarification && qs.length > 0) {
                setQuestions(qs);
                setStage('questioning');
            } else {
                // No clarification needed - proceed directly
                onComplete(originalIdea, false);
            }
        } catch (err) {
            console.error('Clarification evaluation failed:', err);
            // On error, proceed without clarification
            onComplete(originalIdea, false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAnswerSubmit = () => {
        if (!currentAnswer.trim()) return;

        const currentQuestion = questions[currentQuestionIndex];
        const newAnswer = {
            question_id: currentQuestion.id,
            question: currentQuestion.question,
            answer: currentAnswer.trim()
        };

        const updatedAnswers = [...answers, newAnswer];
        setAnswers(updatedAnswers);
        setCurrentAnswer('');

        if (currentQuestionIndex < questions.length - 1) {
            // More questions to go
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            // All questions answered - refine the prompt
            refinePrompt(updatedAnswers);
        }
    };

    const handleOptionSelect = (option) => {
        setCurrentAnswer(option);
        // Auto-submit after a short delay for better UX
        setTimeout(() => {
            const currentQuestion = questions[currentQuestionIndex];
            const newAnswer = {
                question_id: currentQuestion.id,
                question: currentQuestion.question,
                answer: option
            };

            const updatedAnswers = [...answers, newAnswer];
            setAnswers(updatedAnswers);
            setCurrentAnswer('');

            if (currentQuestionIndex < questions.length - 1) {
                setCurrentQuestionIndex(currentQuestionIndex + 1);
            } else {
                refinePrompt(updatedAnswers);
            }
        }, 300);
    };

    const refinePrompt = async (allAnswers) => {
        setIsLoading(true);
        setStage('refining');

        try {
            const headers = isAuthenticated ? getAuthHeaders() : {};
            const response = await axios.post(
                `${BACKEND_URL}/api/clarify/refine`,
                { 
                    original_idea: originalIdea,
                    answers: allAnswers
                },
                { headers }
            );

            setRefinedPrompt(response.data.refined_prompt);
            setStage('reviewing');
        } catch (err) {
            console.error('Prompt refinement failed:', err);
            // Fallback: combine original + answers
            const fallbackPrompt = `${originalIdea}. ${allAnswers.map(a => a.answer).join('. ')}`;
            setRefinedPrompt(fallbackPrompt);
            setStage('reviewing');
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmRefinedPrompt = () => {
        onComplete(refinedPrompt, true);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAnswerSubmit();
        }
    };

    // Render evaluating state
    if (stage === 'evaluating') {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-violet-500/20">
                        <Sparkles className="w-5 h-5 text-violet-400 animate-pulse" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-white">AI App Strategist</h3>
                        <p className="text-sm text-zinc-400">Analyzing your idea...</p>
                    </div>
                </div>
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
                </div>
                <div className="flex justify-end">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onSkip(originalIdea)}
                        className="text-zinc-400 hover:text-white"
                        data-testid="skip-clarification-btn"
                    >
                        <SkipForward className="w-4 h-4 mr-2" />
                        Skip & Generate Now
                    </Button>
                </div>
            </motion.div>
        );
    }

    // Render questioning state (chat-style)
    if (stage === 'questioning') {
        const currentQuestion = questions[currentQuestionIndex];

        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-violet-500/20">
                            <MessageSquare className="w-5 h-5 text-violet-400" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-white">Let's Sharpen Your Idea</h3>
                            <p className="text-xs text-zinc-400">
                                {currentQuestionIndex + 1} of {questions.length} quick questions
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onSkip(originalIdea)}
                        className="text-zinc-400 hover:text-white"
                        data-testid="skip-clarification-btn"
                    >
                        <SkipForward className="w-4 h-4 mr-2" />
                        Skip
                    </Button>
                </div>

                {/* Chat Area */}
                <div className="p-4 max-h-[400px] overflow-y-auto space-y-4">
                    {/* Original idea summary */}
                    <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center shrink-0">
                            <span className="text-xs">You</span>
                        </div>
                        <div className="bg-zinc-800 rounded-xl rounded-tl-none p-3 max-w-[80%]">
                            <p className="text-sm text-zinc-300">{originalIdea}</p>
                        </div>
                    </div>

                    {/* Previous Q&A */}
                    {answers.map((qa, idx) => (
                        <React.Fragment key={idx}>
                            {/* Question */}
                            <div className="flex gap-3 justify-end">
                                <div className="bg-violet-500/20 rounded-xl rounded-tr-none p-3 max-w-[80%]">
                                    <p className="text-sm text-violet-200">{qa.question}</p>
                                </div>
                                <div className="w-8 h-8 rounded-full bg-violet-500/30 flex items-center justify-center shrink-0">
                                    <Sparkles className="w-4 h-4 text-violet-400" />
                                </div>
                            </div>
                            {/* Answer */}
                            <div className="flex gap-3">
                                <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center shrink-0">
                                    <span className="text-xs">You</span>
                                </div>
                                <div className="bg-zinc-800 rounded-xl rounded-tl-none p-3 max-w-[80%]">
                                    <p className="text-sm text-zinc-300">{qa.answer}</p>
                                </div>
                            </div>
                        </React.Fragment>
                    ))}

                    {/* Current question */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex gap-3 justify-end"
                    >
                        <div className="bg-violet-500/20 rounded-xl rounded-tr-none p-3 max-w-[80%]">
                            <p className="text-sm text-violet-200">{currentQuestion.question}</p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-violet-500/30 flex items-center justify-center shrink-0">
                            <Sparkles className="w-4 h-4 text-violet-400" />
                        </div>
                    </motion.div>

                    <div ref={chatEndRef} />
                </div>

                {/* Quick options */}
                {currentQuestion.options && currentQuestion.options.length > 0 && (
                    <div className="px-4 pb-2 flex flex-wrap gap-2">
                        {currentQuestion.options.map((option, idx) => (
                            <button
                                key={idx}
                                onClick={() => handleOptionSelect(option)}
                                className="px-3 py-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-full border border-zinc-700 hover:border-violet-500/50 transition-colors"
                                data-testid={`option-${idx}`}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                )}

                {/* Input area */}
                <div className="p-4 border-t border-zinc-800">
                    <div className="flex gap-2">
                        <input
                            ref={inputRef}
                            type="text"
                            value={currentAnswer}
                            onChange={(e) => setCurrentAnswer(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Type your answer..."
                            className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500"
                            data-testid="clarification-input"
                        />
                        <Button
                            onClick={handleAnswerSubmit}
                            disabled={!currentAnswer.trim()}
                            className="bg-violet-600 hover:bg-violet-500"
                            data-testid="submit-answer-btn"
                        >
                            <Send className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="h-1 bg-zinc-800">
                    <div 
                        className="h-full bg-violet-500 transition-all duration-300"
                        style={{ width: `${((currentQuestionIndex) / questions.length) * 100}%` }}
                    />
                </div>
            </motion.div>
        );
    }

    // Render refining state
    if (stage === 'refining') {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-violet-500/20">
                        <Sparkles className="w-5 h-5 text-violet-400 animate-pulse" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-white">Creating Enhanced Prompt</h3>
                        <p className="text-sm text-zinc-400">Combining your answers...</p>
                    </div>
                </div>
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
                </div>
            </motion.div>
        );
    }

    // Render reviewing state (refined prompt)
    if (stage === 'reviewing') {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-500/20">
                            <Lightbulb className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-white">Your idea just got 10x stronger! 🚀</h3>
                            <p className="text-xs text-zinc-400">Review and edit if needed</p>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsEditing(!isEditing)}
                        className="text-zinc-400 hover:text-white"
                    >
                        <Edit3 className="w-4 h-4 mr-2" />
                        {isEditing ? 'Done Editing' : 'Edit'}
                    </Button>
                </div>

                {/* Refined prompt */}
                <div className="p-4">
                    {isEditing ? (
                        <textarea
                            value={refinedPrompt}
                            onChange={(e) => setRefinedPrompt(e.target.value)}
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-4 text-white min-h-[120px] focus:outline-none focus:border-violet-500"
                            data-testid="refined-prompt-editor"
                        />
                    ) : (
                        <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                            <p className="text-zinc-200 leading-relaxed">{refinedPrompt}</p>
                        </div>
                    )}

                    {/* What was improved */}
                    <div className="mt-4 flex items-start gap-2 text-xs text-zinc-500">
                        <Check className="w-3 h-3 mt-0.5 text-green-400" />
                        <span>Your clarifications have been incorporated into this enhanced prompt.</span>
                    </div>
                </div>

                {/* Actions */}
                <div className="p-4 border-t border-zinc-800 flex gap-3">
                    <Button
                        variant="outline"
                        onClick={() => onSkip(originalIdea)}
                        className="flex-1 border-zinc-700"
                    >
                        Use Original Instead
                    </Button>
                    <Button
                        onClick={handleConfirmRefinedPrompt}
                        className="flex-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500"
                        data-testid="generate-with-refined-btn"
                    >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Blueprint
                        <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </motion.div>
        );
    }

    return null;
};
